import { useCallback, useEffect, useRef, useState } from 'react';
import { UI_TEXT } from '../../config/ui-text';
import { VOICE_CAPTURE } from '../../config/constants';
import { computeAudioLevels, isBlobLongEnough, isReadyChunk } from '../../utils/audioCapture';
import { isSilentTake } from '../../utils/silentTake';
import { frameLevel } from './voiceActivity';
import { appendTranscript } from '../../utils/transcriptAppend';
import { SESSION_TOKEN_PARAM, readSessionToken } from '../../utils/sessionToken';
import { transcribeRejectionFailure } from '../../utils/surveyApi';
import {
  classifyTranscriptionError,
  isUnreadableAudioFailure,
} from '../../utils/transcriptionFailure';

const ERR = UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS;
const AUDIO_LEVEL_BAR_COUNT = 7;
const TRANSCRIBE_CONTEXT_TYPE = 'survey';

/** Ids the transcription request attaches as `context_metadata`, exactly like the pre-extraction
 *  call in `SurveySession.tsx`. A caller omits whichever fields it does not have — `AiDynamicSectionRunner`
 *  has no `trackingId` or `surveyId`, for instance — and they are left off the request rather than sent
 *  as `"undefined"`. */
export interface VoiceOrTextTranscribeContext {
  sessionId?: number;
  surveyId?: number;
  questionId?: string;
  trackingId?: string;
}

export interface UseVoiceCaptureOptions {
  /** Current answer text, read at transcription time via a ref so an append always lands on the
   *  latest keystrokes rather than whatever was current when the recording started. */
  value: string;
  onChange: (value: string) => void;
  /** `forcedBySurvey` marks a switch the respondent did not choose — a recording that did not
   *  work — so the caller can end it with the question instead of keeping it as a preference. */
  onShowTextInputChange: (showTextInput: boolean, forcedBySurvey?: boolean) => void;
  onTranscriptionUnavailable: () => void;

  /** Identifies the answer this capture belongs to (mirrors `answerSlotKey`). A recording or an
   *  in-flight transcription started under a PREVIOUS slotKey is discarded rather than written
   *  into whatever is on screen when it resolves. */
  slotKey: string;

  transcribeContext?: VoiceOrTextTranscribeContext;

  /** Bubbles live recording state up. SurveySession feeds this into the idle-timeout heartbeat,
   *  which must not treat three minutes of talking as three minutes of absence. */
  onRecordingChange?: (isRecording: boolean) => void;

  /** Bubbles a user-facing message up. The caller decides where to show it. */
  onError?: (message: string) => void;

  /**
   * When supplied, stopping the recording HANDS THE CLIP OVER and this hook does nothing else
   * with it — no `/api/transcribe` round trip, no write into `value`.
   *
   * That is the whole of the ordinary spoken auto-advance (Problem 1): the caller submits the
   * answer immediately as `transcription_status: 'pending'`, advances, transcribes the clip
   * itself and patches the text in later keyed by QUESTION ID — the mechanism rapid fire already
   * runs. Delivery by question id is also why the two guards below stop applying to it: the
   * `slotKey` abort and the slot check exist to stop a late transcript landing in whatever answer
   * is on screen now, and a delivery that names its own question cannot do that.
   *
   * A clip too short to be speech is never delivered, so a double-tap on the mic cannot advance.
   */
  onSpokenAnswerCaptured?: (clip: Blob) => void;

  /**
   * The OTHER moment a spoken answer is finished: the words exist and have been written into
   * `value`. Called with the merged value, never the fragment alone, so a caller acting on it can
   * do so without waiting for its own state to catch up.
   *
   * `onSpokenAnswerCaptured` above is the earlier moment — the clip. A caller that can transcribe
   * later and patch (an ordinary survey answer nobody reads until the end) wants that one; a
   * caller whose next step CONSUMES the words wants this one. An `ai_dynamic_section` is the
   * second kind: the prober is handed the answer immediately, so advancing on the clip would hand
   * it an empty string.
   *
   * Never fired for a typed keystroke, and never for an empty transcript — silence drops the
   * respondent into the text box instead (see `transcribeAudio`).
   */
  onSpokenAnswerTranscribed?: (fullValue: string) => void;
}

/** Re-exported so a row-writing caller classifies without importing this hook. */
export { classifyTranscriptionError };

export interface VoiceCaptureState {
  isRecording: boolean;
  isTranscribing: boolean;
  micStatus: 'idle' | 'warming' | 'listening';
  audioLevels: number[];
  /** Recording, but nothing has come through the microphone yet — a dead or muted input. */
  noSignal: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}

/**
 * The mic capture + transcription lifecycle every voice-enabled question in this survey shares:
 * getUserMedia, MediaRecorder, the Web Audio analyser driving the waveform, the mic-readiness
 * timeout, and the transcribe-and-poll round trip against `/api/transcribe`.
 *
 * Pulled out of `VoiceOrTextInput` (which stays the presentational half) so neither file grows
 * past what one screenful of reasoning can hold. This is the subtle part — frontend#659 (a late
 * transcription failure disabling voice for the WRONG question, via the `slotKey` guard below)
 * took real effort to get right once; a second copy anywhere would drift from it silently, which
 * is the whole reason this lives in exactly one hook rather than being copied per caller.
 */
export function useVoiceCapture({
  value,
  onChange,
  onShowTextInputChange,
  onTranscriptionUnavailable,
  slotKey,
  transcribeContext,
  onRecordingChange,
  onError,
  onSpokenAnswerCaptured,
  onSpokenAnswerTranscribed,
}: UseVoiceCaptureOptions): VoiceCaptureState {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [micStatus, setMicStatus] = useState<'idle' | 'warming' | 'listening'>('idle');
  const [audioLevels, setAudioLevels] = useState<number[]>(
    new Array(AUDIO_LEVEL_BAR_COUNT).fill(0),
  );

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const freqBufferRef = useRef<Uint8Array | null>(null);
  const readinessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readinessFailedRef = useRef(false);
  const transcriptionAbortRef = useRef<AbortController | null>(null);
  const [noSignal, setNoSignal] = useState(false);
  const takeStartedAtRef = useRef(0);
  const heardSignalRef = useRef(false);
  /** Consecutive silent takes on ONE answer slot; the tally resets with the slot — ADR-0014. */
  const silentTakesRef = useRef<{ slotKey: string; count: number }>({ slotKey: '', count: 0 });

  // Mirrors slotKey for the async transcription callback, which resolves seconds later and must
  // not read a stale closure - see the docblock on the `slotKey` option.
  const slotKeyRef = useRef(slotKey);
  useEffect(() => {
    slotKeyRef.current = slotKey;
  }, [slotKey]);

  // Same reason as transcribeAudioRef below: `startRecording` is a stable ([]) callback, and its
  // `onstop` has to reach the LATEST handler rather than the one that existed at mount.
  const onSpokenAnswerCapturedRef = useRef(onSpokenAnswerCaptured);
  const onSpokenAnswerTranscribedRef = useRef(onSpokenAnswerTranscribed);
  useEffect(() => {
    onSpokenAnswerCapturedRef.current = onSpokenAnswerCaptured;
  }, [onSpokenAnswerCaptured]);
  useEffect(() => {
    onSpokenAnswerTranscribedRef.current = onSpokenAnswerTranscribed;
  }, [onSpokenAnswerTranscribed]);

  // Cancels the status poll of a transcription the respondent has walked away from. Cleanup fires
  // on every slot change AND on unmount; deliberately NOT wired to the upload POST, so the clip
  // still reaches the backend and is still transcribed and stored server-side - only the client
  // stops waiting on it.
  useEffect(() => {
    return () => {
      transcriptionAbortRef.current?.abort();
      transcriptionAbortRef.current = null;
    };
  }, [slotKey]);

  const setRecording = useCallback(
    (next: boolean) => {
      setIsRecording(next);
      onRecordingChange?.(next);
    },
    [onRecordingChange],
  );

  // Routed through a ref rather than naming `analyzeAudio` inside its own body: the animation loop
  // recurses every frame via requestAnimationFrame, and referencing the useCallback binding
  // directly from inside its own initializer is a self-reference lint flags (it cannot tell the
  // recursive call only ever runs after the binding is complete).
  const analyzeAudioRef = useRef<() => void>(() => {});
  const analyzeAudio = useCallback(() => {
    if (!analyserRef.current) return;
    const analyser = analyserRef.current;

    if (!freqBufferRef.current || freqBufferRef.current.length !== analyser.frequencyBinCount) {
      freqBufferRef.current = new Uint8Array(analyser.frequencyBinCount);
    }
    const dataArray = freqBufferRef.current;
    analyser.getByteFrequencyData(dataArray);

    setAudioLevels(computeAudioLevels(dataArray, AUDIO_LEVEL_BAR_COUNT));
    if (frameLevel(dataArray) >= VOICE_CAPTURE.NO_SIGNAL_LEVEL) heardSignalRef.current = true;
    const waitedMs = Date.now() - takeStartedAtRef.current;
    setNoSignal(!heardSignalRef.current && waitedMs >= VOICE_CAPTURE.NO_SIGNAL_HINT_MS);
    animationFrameRef.current = requestAnimationFrame(() => analyzeAudioRef.current());
  }, []);
  useEffect(() => {
    analyzeAudioRef.current = analyzeAudio;
  }, [analyzeAudio]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (readinessTimerRef.current) {
        clearTimeout(readinessTimerRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const pollTranscriptionResult = useCallback(
    async (taskId: string, signal?: AbortSignal): Promise<string> => {
      const maxAttempts = 30;
      const pollInterval = 1000;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (signal?.aborted) {
          throw new Error('Transcription abandoned');
        }

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/transcribe/status/${taskId}`,
          { signal },
        );
        if (!response.ok) {
          throw new Error('Failed to check transcription status');
        }

        const result = await response.json();
        if (result.status === 'completed') {
          return result.text || '';
        }
        if (result.status === 'failed') {
          throw new Error(result.error || 'Transcription failed');
        }

        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, pollInterval);
          signal?.addEventListener(
            'abort',
            () => {
              clearTimeout(timer);
              resolve();
            },
            { once: true },
          );
        });
      }

      throw new Error('Transcription timeout');
    },
    [],
  );

  const transcribeAudio = useCallback(
    async (audioBlob: Blob, mimeType: string, recordedForSlot: string) => {
      let reachedServer = false;
      try {
        setIsTranscribing(true);

        const getFileExtension = (type: string): string => {
          if (type.includes('webm')) return '.webm';
          if (type.includes('mp4')) return '.mp4';
          if (type.includes('wav')) return '.wav';
          if (type.includes('mp3')) return '.mp3';
          if (type.includes('m4a')) return '.m4a';
          return '.webm';
        };

        const formData = new FormData();
        formData.append('audio', audioBlob, `recording${getFileExtension(mimeType)}`);

        if (transcribeContext?.sessionId !== undefined) {
          formData.append('context_type', TRANSCRIBE_CONTEXT_TYPE);
          formData.append(
            'context_metadata',
            JSON.stringify({
              session_id: String(transcribeContext.sessionId),
              ...(transcribeContext.surveyId !== undefined
                ? { survey_id: String(transcribeContext.surveyId) }
                : {}),
              ...(transcribeContext.questionId
                ? { question_id: String(transcribeContext.questionId) }
                : {}),
              ...(transcribeContext.trackingId
                ? { tracking_id: String(transcribeContext.trackingId) }
                : {}),
            }),
          );
          // ADR-0011: unauthenticated route — the token is what lets the server act on the id.
          const sessionToken = readSessionToken(transcribeContext.sessionId);
          if (sessionToken) {
            formData.append(SESSION_TOKEN_PARAM, sessionToken);
          }
        }

        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe`, {
          method: 'POST',
          body: formData,
        });
        // Even a non-2xx reply means the server saw it — ADR-0011.
        reachedServer = true;

        if (!response.ok) {
          throw await transcribeRejectionFailure(response);
        }

        const result = await response.json();

        if (result.status !== 'processing' || !result.task_id) {
          throw new Error(result.error || 'Transcription failed - no task ID returned');
        }

        const abortController = new AbortController();
        transcriptionAbortRef.current = abortController;
        const transcribedText = await pollTranscriptionResult(
          result.task_id,
          abortController.signal,
        );

        // A transcript may resolve seconds after the respondent moved on. Writing it into whatever
        // slot is on screen now would put words the respondent never said about it into that
        // answer, so drop it instead.
        if (slotKeyRef.current !== recordedForSlot) {
          return;
        }

        if (!transcribedText) {
          // Empty transcript = silence or unintelligible audio. Drop the respondent into the text
          // box so they can see what survived and type the rest. Deliberately NOT flagging
          // transcriptionUnavailable: one unintelligible clip doesn't mean the service is down.
          onShowTextInputChange(true, true);
          onError?.(ERR.TRANSCRIPTION_EMPTY);
          return;
        }

        const merged = appendTranscript(value, transcribedText);
        onChange(merged);
        onSpokenAnswerTranscribedRef.current?.(merged);
      } catch (error) {
        // Same slot-guard as the success path, for a sharper reason: transcriptionUnavailable is a
        // ONE-WAY kill switch. A clip abandoned slots ago failing must not strip voice from the
        // slot now on screen (frontend#659).
        if (slotKeyRef.current !== recordedForSlot) {
          return;
        }
        console.error(
          `Voice transcription failed (${classifyTranscriptionError(error, reachedServer)}):`,
          error,
        );
        // A bad blob is not a broken service, and the kill switch above is one-way — ADR-0011.
        if (isUnreadableAudioFailure(error)) {
          onShowTextInputChange(true, true);
          onError?.(ERR.RECORDING_UNREADABLE);
          return;
        }
        onTranscriptionUnavailable();
        onShowTextInputChange(true, true);
        onError?.(ERR.TRANSCRIPTION_UNAVAILABLE);
      } finally {
        setIsTranscribing(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      value,
      transcribeContext,
      onChange,
      onError,
      onShowTextInputChange,
      onTranscriptionUnavailable,
    ],
  );

  // startRecording is a stable ([]) callback, but its onstop must call the LATEST transcribeAudio
  // (which closes over `value` for the append). Routed through a ref so we get the current binding
  // without rebuilding startRecording or fighting the declaration order.
  const transcribeAudioRef = useRef(transcribeAudio);
  useEffect(() => {
    transcribeAudioRef.current = transcribeAudio;
  }, [transcribeAudio]);

  const startRecording = useCallback(async () => {
    // Pin the answer slot at the moment the respondent starts speaking - everything downstream is
    // async, so this is the only point where "which slot is this for" is unambiguous.
    const recordedForSlot = slotKeyRef.current;
    readinessFailedRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });

      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : MediaRecorder.isTypeSupported('audio/wav')
            ? 'audio/wav'
            : '';

      if (!mimeType) {
        throw new Error('No supported audio format found');
      }

      type WindowWithWebkitAudioContext = typeof window & {
        webkitAudioContext?: typeof AudioContext;
      };
      const AudioContextCtor =
        window.AudioContext || (window as WindowWithWebkitAudioContext).webkitAudioContext;
      if (!AudioContextCtor) {
        throw new Error('AudioContext not supported');
      }
      const audioContext = new AudioContextCtor();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.1;
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      takeStartedAtRef.current = Date.now();
      heardSignalRef.current = false;
      animationFrameRef.current = requestAnimationFrame(analyzeAudio);

      const mediaRecorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (isReadyChunk(event.data.size)) {
          audioChunksRef.current.push(event.data);
          // First real chunk = proof capture is actually live. Only NOW is it safe to tell the
          // respondent to speak.
          if (readinessTimerRef.current) {
            clearTimeout(readinessTimerRef.current);
            readinessTimerRef.current = null;
            setMicStatus('listening');
            setRecording(true);
          }
        }
      };

      mediaRecorder.onstop = async () => {
        if (readinessFailedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

        if (!isBlobLongEnough(audioBlob.size, VOICE_CAPTURE.MIN_BLOB_BYTES)) {
          onError?.(ERR.RECORDING_TOO_SHORT);
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        // Measured on the clip, not the live analyser, and BEFORE either consumer — ADR-0014.
        if (await isSilentTake(audioBlob)) {
          const tally = silentTakesRef.current;
          const count = tally.slotKey === recordedForSlot ? tally.count + 1 : 1;
          silentTakesRef.current = { slotKey: recordedForSlot, count };
          onError?.(ERR.NO_SOUND_DETECTED);
          if (count >= VOICE_CAPTURE.SILENT_TAKE_LIMIT) onShowTextInputChange(true, true);
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        silentTakesRef.current = { slotKey: '', count: 0 };

        // The caller owns this clip from here. Deliberately BEFORE the inline transcription, and
        // returning: running both would transcribe one recording twice and write two answers for
        // one question, the second of them into whichever slot is on screen by then.
        if (onSpokenAnswerCapturedRef.current) {
          onSpokenAnswerCapturedRef.current(audioBlob);
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        await transcribeAudioRef.current?.(audioBlob, mimeType, recordedForSlot);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.onerror = () => {
        if (readinessTimerRef.current) {
          clearTimeout(readinessTimerRef.current);
          readinessTimerRef.current = null;
        }
        onError?.(ERR.RECORDING_FAILED);
        setMicStatus('idle');
        setRecording(false);
        stream.getTracks().forEach((track) => track.stop());
      };

      setMicStatus('warming');
      mediaRecorder.start(VOICE_CAPTURE.TIMESLICE_MS);

      // If no audio chunk ever arrives, capture never came up (slow/blocked device): stop cleanly
      // and drop the respondent to typing rather than a dead recording.
      readinessTimerRef.current = setTimeout(() => {
        readinessTimerRef.current = null;
        readinessFailedRef.current = true;
        try {
          if (mediaRecorder.state === 'recording') mediaRecorder.stop();
        } catch {
          /* noop */
        }
        stream.getTracks().forEach((track) => track.stop());
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        audioChunksRef.current = [];
        setMicStatus('idle');
        setRecording(false);
        onShowTextInputChange(true, true);
        onError?.(ERR.MIC_NOT_READY);
      }, VOICE_CAPTURE.READINESS_TIMEOUT_MS);
    } catch {
      onError?.(ERR.MIC_PERMISSION);
      setMicStatus('idle');
      setRecording(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (readinessTimerRef.current) {
      clearTimeout(readinessTimerRef.current);
      readinessTimerRef.current = null;
    }
    setMicStatus('idle');

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    audioContextRef.current = null;
    analyserRef.current = null;
    setAudioLevels(new Array(AUDIO_LEVEL_BAR_COUNT).fill(0));
    setNoSignal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isRecording,
    isTranscribing,
    micStatus,
    audioLevels,
    noSignal,
    startRecording,
    stopRecording,
  };
}
